import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, X, Lock } from "lucide-react";
import { IoBusinessOutline } from "react-icons/io5";
import axios from "utils/axios";
import { PiHouse, PiCalendarBlank } from "react-icons/pi";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";

const formatDate = (dateString) => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // Use AM/PM format
    timeZone: "Australia/Sydney",
  }).format(new Date(dateString));
};

const FilterDropdown = ({
  options,
  selectedOption,
  onOptionSelect,
  placeholder = "Select an option...",
  allOption = { _id: "all", name: "All" },
  imageKey = null, // Key for the image source (optional)
  labelKey = "name", // Key for the main display label
  secondaryLabelKey = null, // Key for an additional label (optional)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Search term for filtering
  const dropdownRef = useRef(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    onOptionSelect(option);
    setIsOpen(false);
    setSearchTerm(""); // Reset search term
  };

  const filteredOptions = options.filter((option) =>
    option[labelKey].toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={dropdownRef} className="w-full max-w-sm relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between border border-mediumgray rounded px-4 py-1.5 text-sm focus:outline-none"
      >
        {selectedOption ? (
          <div className="flex items-center gap-3">
            {imageKey && selectedOption[imageKey] && (
              <>
                <img
                  src={selectedOption[imageKey]}
                  alt={selectedOption[labelKey]}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: selectedOption["calendarColor"] }}
                ></div>
              </>
            )}
            <div className="flex flex-col items-start">
              <span className="font-medium">{selectedOption[labelKey]}</span>
              {secondaryLabelKey && (
                <span className="text-sm text-gray-500">
                  {selectedOption[secondaryLabelKey]}
                </span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-darkergray">{placeholder}</span>
        )}
        <i className="fas fa-chevron-down text-xs"></i>
      </button>

      {isOpen && (
        <div className="absolute mt-1 w-full bg-white border border-mediumgray rounded z-10">
          <div className="p-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none"
            />
          </div>
          <ul className="py-1 max-h-60 overflow-auto m-0">
            <li>
              <button
                onClick={() => handleSelect(allOption)}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              >
                <span className="font-medium">{allOption[labelKey]}</span>
              </button>
            </li>
            {filteredOptions.map((option) => (
              <li key={option._id}>
                <button
                  onClick={() => handleSelect(option)}
                  className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                >
                  {imageKey && option[imageKey] && (
                    <>
                      <img
                        src={option[imageKey]}
                        alt={option[labelKey]}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div
                        className="w-6 h-6 rounded"
                        style={{
                          backgroundColor: option["calendarColor"],
                        }}
                      ></div>
                    </>
                  )}
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{option[labelKey]}</span>
                    {secondaryLabelKey && (
                      <span className="text-sm text-gray-500">
                        {option[secondaryLabelKey]}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Updated ImageUpload Component for the Modal
const ImageUpload = ({ image, setImage, propertyId }) => {
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Create object URL when image changes
  useEffect(() => {
    // Clean up previous object URL to prevent memory leaks
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    // Create new object URL if image is a proper File object
    if (image && !image.url && image instanceof File) {
      const url = URL.createObjectURL(image);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }

    // Cleanup function
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [image]);

  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);

      // If propertyId is provided, upload the file immediately
      if (propertyId) {
        await uploadFile(file);
      }
    }
  };

  const uploadFile = async (file) => {
    if (!propertyId) return;

    setIsUploading(true);
    const fileId = Date.now().toString(); // Generate unique ID for tracking progress

    try {
      const uploadedImageData = await processFileUpload(file, fileId);
      // After successful upload, notify parent component
      if (uploadedImageData) {
        setImage(uploadedImageData);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Then modify the processFileUpload function to return the uploaded image data
  const processFileUpload = async (file, id) => {
    try {
      // Step 1: Get presigned URL
      const presignedUrlResponse = await axios.post(
        "/admin/prepareMarketing/fileUpload",
        {
          files: [{ fileName: file.name, fileType: file.type }],
          propertyId,
          folderPath: "marketingItems",
        }
      );

      const { presignedUrls } = presignedUrlResponse.data;
      const presignedUrl = presignedUrls[0];

      // Step 2: Upload file to S3
      await axios.put(presignedUrl.url, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress((prev) => ({ ...prev, [id]: percentage }));
        },
      });

      // Helper function to extract the base URL without query parameters
      function extractBaseUrl(fullUrl) {
        // Remove query parameters (everything after '?')
        return fullUrl.split("?")[0];
      }

      // Create the uploaded image data
      const uploadedImageData = {
        name: file.name,
        type: file.type,
        s3Key: presignedUrl.key,
        url: extractBaseUrl(presignedUrl.url),
        isUploaded: true,
      };

      // Return the uploaded image data
      return uploadedImageData;
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      throw error;
    }
  };

  const removeImage = () => {
    setImage(null);
    setUploadProgress({});
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full mt-4">
      <p className="text-sm font-medium mb-2">Upload Completed Work Image</p>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        {image ? (
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-md mb-2">
              <img
                src={image.url || previewUrl}
                alt="Preview"
                className="w-full h-48 object-contain rounded"
              />
              <button
                onClick={removeImage}
                className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Show upload progress if uploading */}
              {isUploading && Object.keys(uploadProgress).length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2">
                  <div className="w-full bg-lightgray rounded-full h-2.5">
                    <div
                      className="bg-black h-2 rounded-full"
                      style={{ width: `${Object.values(uploadProgress)[0]}%` }}
                    ></div>
                  </div>
                  <p className="text-white text-xs text-center mt-1">
                    {Object.values(uploadProgress)[0]}%
                  </p>
                </div>
              )}
            </div>
            <p className="text-sm darkergray">{image.name}</p>
            {image.isUploaded && (
              <span className="text-xs text-green-500 mt-1">
                ✓ Uploaded successfully
              </span>
            )}
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center cursor-pointer py-6"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="bg-lightergray p-3 rounded-full mb-2">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                ></path>
              </svg>
            </div>
            <p className="text-sm font-medium">Click to upload</p>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG or JPEG (max. 10MB)
            </p>
          </div>
        )}
        <input
          type="file"
          className="hidden"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageChange}
        />
      </div>
    </div>
  );
};

const Prints = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState({});
  const [activeColumn, setActiveColumn] = useState(null);

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({});

  // Added missing states for modal and completion functionality
  const [completeModal, setCompleteModal] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [image, setImage] = useState(null);

  const [properties, setProperties] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/admin/marketingOrders");

        const { data } = response.data;

        const transformedColumns = Object.entries(data).reduce(
          (acc, [status, items]) => {
            acc[status] = {
              name: status.charAt(0).toUpperCase() + status.slice(1),
              bgColor: getStatusBgColor(status),
              color: getStatusTextColor(status),
              items,
            };
            return acc;
          },
          {}
        );

        setColumns(transformedColumns);
        setActiveColumn(Object.keys(transformedColumns)[0]);
      } catch (error) {
        console.error("Error fetching kanban data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`/admin/events/uniqueProperties`);
        if (response.data.success) {
          setProperties(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching agents", error);
      }
    };

    fetchProperties();
  }, []);

  const [selectedProperty, setSelectedProperty] = useState(null);

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
  };

  const getStatusBgColor = (status) => {
    const colors = {
      overdue: "bg-red-50",
      pending: "bg-orange-50",
      inProgress: "bg-lime-50",
      completed: "bg-green-50",
      delivered: "bg-blue-50",
      installed: "bg-blue-50",
      repairSent: "bg-red-50",
      repairInProgress: "bg-lime-50",
      repairCompleted: "bg-green-50",
      removeSent: "bg-red-50",
      removeInProgress: "bg-lime-50",
      removeCompleted: "bg-green-50",
    };
    return colors[status] || "bg-gray-50";
  };

  const getStatusTextColor = (status) => {
    const colors = {
      overdue: "text-red-600",
      pending: "text-orange-600",
      inProgress: "text-lime-600",
      completed: "text-green-600",
      delivered: "text-blue-600",
      installed: "text-blue-600",
      repairSent: "text-red-600",
      repairInProgress: "text-lime-600",
      repairCompleted: "text-green-600",
      removeSent: "text-red-600",
      removeInProgress: "text-lime-600",
      removeCompleted: "text-green-600",
    };
    return colors[status] || "text-gray-600";
  };

  const handlePrevColumn = () => {
    const columnKeys = Object.keys(columns);
    const currentIndex = columnKeys.indexOf(activeColumn);
    if (currentIndex > 0) {
      setActiveColumn(columnKeys[currentIndex - 1]);
    }
  };

  const handleNextColumn = () => {
    const columnKeys = Object.keys(columns);
    const currentIndex = columnKeys.indexOf(activeColumn);
    if (currentIndex < columnKeys.length - 1) {
      setActiveColumn(columnKeys[currentIndex + 1]);
    }
  };

  const addressMatch = (a = "", b = "") =>
    a.toLowerCase().includes(b.toLowerCase()) || b.toLowerCase().includes(a.toLowerCase());
  
  const filteredColumns = Object.entries(columns).reduce(
    (acc, [columnId, column]) => {
      acc[columnId] = {
        ...column,
        items:
          selectedProperty && selectedProperty._id !== "all"
            ? column.items.filter((item) =>
                addressMatch(item.propertyAddress, selectedProperty.address)
              )
            : column.items,
      };
      return acc;
    },
    {}
  );
  

  const handleDownload = async (_id, orderId) => {
    try {
      setDownloadLoading((prev) => ({ ...prev, [_id]: true }));
      const response = await axios.post(
        "/admin/marketingOrders/download",
        { _id: orderId },
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/zip" });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "marketingItems.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading ZIP:", error.message);
    } finally {
      setDownloadLoading((prev) => ({ ...prev, [_id]: false }));
    }
  };

  // Handle marking item as complete with S3 upload
  const handleMarkComplete = async () => {

    if (!image || !selectedItem) {
      console.log("Missing image or selectedItem");
      return;
    }

    if (!image.isUploaded) {
      console.log("Image not fully uploaded yet");
      return;
    }

    setCompleteLoading(true);
    try {
      // Upload logic for S3 appears to be missing here
      // Assuming you have an s3Key variable from earlier in the code

      // Make API call to mark as complete
      await axios.put(`/admin/marketingOrders/${selectedItem?.orderId}`, {
        [`products.${selectedItem.index}`]: {
          productId: selectedItem.productId,
          productName: selectedItem.productName,
          downloadUrl: selectedItem.downloadUrl,
          qty: selectedItem.qty,
          installationPhotos: image.url,
          status: "completed",
        },
      });

      const updatedColumns = { ...columns };

      // Remove item from ALL columns
      Object.keys(updatedColumns).forEach((columnKey) => {
        if (columnKey !== "completed") {
          // Skip the completed column
          updatedColumns[columnKey].items = updatedColumns[
            columnKey
          ].items.filter((item) => item._id !== selectedItem._id);
        }
      });

      // Add item to completed column if it exists
      if (updatedColumns.completed) {
        const updatedItem = {
          ...selectedItem,
          products: selectedItem.products.map((product) => ({
            ...product,
            downloadUrl: image.url,
            status: "completed",
          })),
          status: "completed",
        };

        updatedColumns.completed.items = [
          updatedItem,
          ...updatedColumns.completed.items,
        ];
      }

      setColumns(updatedColumns);
      setCompleteModal(false);
      setImage(null);
      setSelectedItem(null);
    } catch (error) {
      console.error("Error marking as complete:", error);
    } finally {
      setCompleteLoading(false);
      setCompleteModal(false);
    }
  };

  // Function to open modal with selected item
  const openCompleteModal = (item) => {
    setSelectedItem(item);
    setCompleteModal(true);
  };

  if (loading) {
    return <PageLoader />;
  }

  const columnsList = Object.entries(columns);
  const currentColumnIndex = columnsList.findIndex(
    ([id]) => id === activeColumn
  );

  const filteredColumnsList = Object.entries(filteredColumns);
  const currentColumn = filteredColumnsList[currentColumnIndex]?.[1];

  return (
    <div className="container space-y-2">
      <FilterDropdown
        options={properties}
        selectedOption={selectedProperty}
        onOptionSelect={(option) => handlePropertySelect(option)}
        placeholder="Select a property..."
        allOption={{ _id: "all", address: "Select all Properties" }}
        imageKey={null}
        labelKey="address"
      />
      <div className="w-full h-[75vh] relative bg-gray-50">
        {/* Desktop View */}
        <div className="hidden md:block h-full">
          <div className="flex gap-4 p-4 h-full overflow-x-auto">
            {Object.entries(filteredColumns).map(([columnId, column]) => (
              <div
                key={columnId}
                className="flex-shrink-0 w-80 bg-gray-50 rounded-lg overflow-y-auto max-h-[600px]"
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`${column.bgColor} ${column.color} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2`}
                    >
                      {column.name
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                      {column.locked && <Lock className="w-3 h-3" />}
                    </span>
                    <span className="text-sm text-gray-500">
                      {column.items.length}
                    </span>
                  </div>
                </div>

                <div className="p-2 space-y-3 min-h-[calc(100%-5rem)] bg-gray-100">
                  {column?.items.map((item, index) => (
                    <div
                      key={index}
                      className="cursor-pointer p-4 bg-white rounded-lg border shadow-sm"
                    >
                      <div className="text-xs">{item.productName}</div>

                      <div className="mt-2 flex gap-2 items-center">
                        <PiCalendarBlank className="w-4 h-4 text-darkergray" />
                        <p className="text-xs">{formatDate(item?.createdAt)}</p>
                      </div>

                      <div className="mt-2 flex gap-2 items-center">
                        <span className="text-xs text-darkergray">
                          Property:{" "}
                        </span>
                        <p className="text-xs">{item?.propertyAddress}</p>
                      </div>

                      <div className="mt-2 flex gap-2 items-center">
                        <span className="text-xs text-darkergray">
                          Delivery:{" "}
                        </span>
                        <p className="text-xs">{item?.deliveryAddress}</p>
                      </div>

                      <div className="mt-2 flex gap-2 items-center">
                        <IoBusinessOutline className="w-4 h-4 text-darkergray" />
                        <p className="text-xs">{item?.company}</p>
                      </div>

                      <div className="mt-4 flex justify-between">
                        <Button
                          className="gray-button px-2 py-1 text-xs"
                          onClick={() =>
                            handleDownload(item?._id, item?.orderId)
                          }
                          loading={downloadLoading[item._id]}
                          disabled={downloadLoading[item._id]}
                        >
                          Download
                        </Button>

                        <Button
                          className="black-button px-2 py-1 text-xs"
                          onClick={() => openCompleteModal(item)}
                          disabled={columnId === "completed"}
                        >
                          Mark as completed
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden h-full flex flex-col">
          <div className="flex items-center justify-between p-4 bg-white border-b">
            <button
              onClick={handlePrevColumn}
              disabled={currentColumnIndex === 0}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-2">
              <span
                className={`${currentColumn?.bgColor} ${currentColumn?.color} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2`}
              >
                {currentColumn?.name
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </span>
              <span className="text-sm text-gray-500">
                {currentColumnIndex + 1} of {columnsList.length}
              </span>
            </div>

            <button
              onClick={handleNextColumn}
              disabled={currentColumnIndex === columnsList.length - 1}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {currentColumn?.items.map((item) => (
              <div
                key={item.id || item._id}
                className={`bg-white p-4 rounded-lg border shadow-sm space-y-2 ${
                  currentColumn.locked ? "opacity-90" : ""
                }`}
              >
                <div className="text-xs">{item.productName}</div>

                <div className="mt-2 flex gap-2 items-center">
                  <PiCalendarBlank className="w-4 h-4 text-darkergray" />
                  <p className="text-xs">{formatDate(item?.createdAt)}</p>
                </div>

                <div className="mt-2 flex gap-2 items-center">
                  <span className="text-xs text-darkergray">Property: </span>
                  <p className="text-xs">{item?.propertyAddress}</p>
                </div>

                <div className="mt-2 flex gap-2 items-center">
                  <span className="text-xs text-darkergray">Delivery: </span>
                  <p className="text-xs">{item?.deliveryAddress}</p>
                </div>

                <div className="mt-2 flex gap-2 items-center">
                  <IoBusinessOutline className="w-4 h-4 text-darkergray" />
                  <p className="text-xs">{item?.company}</p>
                </div>

                <div className="mt-4 flex justify-between">
                  <Button
                    className="gray-button px-2 py-1 text-xs"
                    onClick={() => handleDownload(item?._id)}
                    loading={downloadLoading[item._id]}
                    disabled={downloadLoading[item._id]}
                  >
                    Download
                  </Button>
                  <Button
                    className="black-button px-2 py-1 text-xs"
                    onClick={() => openCompleteModal(item)}
                    disabled={activeColumn === "completed"}
                  >
                    Mark as completed
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Completion Modal */}
      <Modal
        isOpen={completeModal}
        onClose={() => setCompleteModal(false)}
        title="Confirm Completion"
      >
        <div className="flex flex-col items-center justify-center">
          <p>Are you sure you want to mark as completed?</p>
          {selectedItem && (
            <ImageUpload
              image={image}
              setImage={setImage}
              propertyId={selectedItem.propertyId}
            />
          )}
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={() => setCompleteModal(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button
              onClick={handleMarkComplete}
              className="black-button"
              disabled={completeLoading || !image || !image.isUploaded}
              loading={completeLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Prints;
